// Client-side data loader. All JSON is pre-built by the Python pipeline and
// served from /data/*.json. We fetch on first use and cache.

import type {
  AllData,
  Dimension,
  Incident,
  PlaybookEntry,
  Quote,
  Recognition,
  Response,
  StatsData,
} from './types';

let cache: AllData | null = null;
let loadingPromise: Promise<AllData> | null = null;

async function fetchJson<T>(name: string): Promise<T> {
  const res = await fetch(`/data/${name}.json`);
  if (!res.ok) {
    throw new Error(`Failed to load ${name}.json: ${res.status}`);
  }
  return res.json() as Promise<T>;
}

export async function loadAll(): Promise<AllData> {
  if (cache) return cache;
  if (loadingPromise) return loadingPromise;

  loadingPromise = (async () => {
    const [
      dimensions,
      playbook,
      quotes,
      responses,
      incidents,
      recognition,
      stats,
      testimonials,
      meta,
    ] = await Promise.all([
      fetchJson<Dimension[]>('dimensions'),
      fetchJson<PlaybookEntry[]>('playbook'),
      fetchJson<Quote[]>('quotes'),
      fetchJson<Response[]>('responses'),
      fetchJson<Incident[]>('incidents'),
      fetchJson<Recognition[]>('recognition'),
      fetchJson<StatsData>('stats'),
      fetchJson<Quote[]>('testimonials'),
      fetchJson<{ buildTime: string }>('meta'),
    ]);
    cache = {
      dimensions,
      playbook,
      quotes,
      responses,
      incidents,
      recognition,
      stats,
      testimonials,
      buildTime: meta.buildTime,
    };
    return cache;
  })();

  return loadingPromise;
}

// React hook — suspends the render until data is ready (uses Suspense-friendly
// "throw promise" pattern).
export function useAllData(): AllData {
  if (cache) return cache;
  if (!loadingPromise) loadAll();
  throw loadingPromise;
}

// Convenience lookup helpers
export function getDimension(data: AllData, code: string): Dimension | undefined {
  return data.dimensions.find((d) => d.code === code);
}

export function getPlaybookEntry(data: AllData, code: string): PlaybookEntry | undefined {
  return data.playbook.find((p) => p.dimension === code);
}

export function getQuote(data: AllData, id: string): Quote | undefined {
  return data.quotes.find((q) => q.id === id);
}

export function getQuotesForDimension(data: AllData, code: string): Quote[] {
  return data.quotes.filter((q) => q.dimension === code);
}

export function getResponse(data: AllData, rowId: number): Response | undefined {
  return data.responses.find((r) => r.rowId === rowId);
}

export function getDimensionsByAxis(
  data: AllData,
  axis: 'op' | 'sn' | 'st' | 'mo',
): Dimension[] {
  return data.dimensions.filter((d) => d.axis === axis);
}
